/**
 * File: category_manager.tsx
 * Description: Admin component that renders a freeSolo MUI Autocomplete for selecting or creating
 * a FAQ category. Supports picking an existing category by title or typing a brand-new one,
 * and normalises both cases into a unified CategorySelection value. Includes empty-field
 * validation that activates after the user first interacts with the input.
 * 
 * TODO -> Fix render problem. When this component is unmount a mount very fast it keep its value 
 *    (See tests A5.3 and A5.5 in frontend_vigo_faq/test/pages/admin/admin_page_create_article_submission.test.tsx).
 * 
 */
import { useState } from 'react';
import type { Category, CategorySelection } from '@/interfaces/category';
import {
  Box,
  Autocomplete,
  TextField,
} from '@mui/material';

/**
 * Props for the CategoryManager component.
 *
 * @property {Category[]} categories - Full list of existing categories used to populate the autocomplete options.
 * @property {(selection: CategorySelection | null) => void} onChange - Callback invoked whenever the selection changes. Receives null when cleared.
 */
interface CategoryManagerProps {
  categories: Category[];
  onChange: (selection: CategorySelection | null) => void;
}

function CategoryManager({ categories, onChange }: CategoryManagerProps) {
  
  const [selectCategory, setSelectCategory] = useState<Category | null>(null);
  const [createCategoryTitle, setCreateCategoryTitle] = useState('');
  const [isCreateError, setIsCreateError] = useState(false);

  /**
   * Handles a change on the "existing category" Autocomplete.
   * Propagates the resolved CategorySelection to the parent, or null when the selection is cleared.
   *
   * @param {React.SyntheticEvent} _event - MUI Autocomplete change event, unused.
   * @param {Category | null} newValue - The category picked from the list, or null when cleared.
   * @returns {void}
   */
  function handleSelectChange(_event: React.SyntheticEvent, newValue: Category | null) {
    if (newValue === null) {
      onChange(null);
      setSelectCategory(null)
    } else {
      onChange({ categoryId: newValue.id, categoryTitle: newValue.title });
      setSelectCategory(newValue)
    }
  }

  /**
   * Handles a change on the "create category" text field.
   * Guards against creating a duplicate category by checking the trimmed input against
   * existing category titles, setting the error state when a match is found instead of
   * propagating the selection.
   *
   * @param {React.SyntheticEvent} _event - Field change/blur event, unused.
   * @param {string} newValue - Current raw value of the text field.
   * @returns {void}
   */
  function handleCreateChange(_event: React.SyntheticEvent, newValue: string) {
    if (newValue === '') {
      onChange(null);
      setCreateCategoryTitle('')
      setIsCreateError(false)
    } else {
      const existingCategory = categories.find((category) => category.title === newValue.trim())
      if (existingCategory){
        onChange(null);
        setIsCreateError(true)
      } else {
        setIsCreateError(false)
        onChange({ categoryId: null, categoryTitle: newValue.trim() });
      }
      setCreateCategoryTitle(newValue)
    }
  }

  return (
    <Box>
      <Autocomplete
        options={categories}
        value={selectCategory}
        onChange={handleSelectChange}
        getOptionLabel={(option) => option.title}
        isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
        disabled={createCategoryTitle !== ''}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Seleccionar Categoría Existente"
            helperText={createCategoryTitle !== '' ? 'Campo desactivado, se va crear una nueva categoría' : ''}
          />
        )}
      />
      <TextField 
        id="create-categorie-outlined"
        label="Crear Nueva Categoría" 
        variant="outlined" 
        value={createCategoryTitle}
        onChange={(event) => { handleCreateChange(event, event.target.value)} }
        onBlur={(event) => { handleCreateChange(event, event.target.value)} }
        disabled={selectCategory !== null}
        sx={{ marginTop:1, width:"100%" }}
        error={isCreateError}
        helperText={
          isCreateError ? 'Esta categoría ya exite, elíjala en el campo "Seleccionar Categoría Existente"' 
          : selectCategory !== null ? 'Campo desactivado, se a selecciona una categoría existente' : ''}
      />
    </Box>
  );
}

export default CategoryManager;
